import { useLocalStorageState, useMount } from "ahooks";
import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import {
  LanguageApi,
  DataTranslationValueDto,
  DataTranslationDto,
} from "../../api";
import { useEventsContext } from "../../contexts";

import { useApiService } from "../useApiService";
import { useRouter } from "../useRouter";

export function useLanguages() {
  const languageApi = useApiService(LanguageApi);
  const { dispatchEvent } = useEventsContext();
  const { i18n } = useTranslation();
  const [languageFromStorage, setLanguageFromStorage] = useLocalStorageState(
    "language",
    { defaultValue: "fr" }
  );

  const { data, isFetching, refetch } = useQuery(
    "ref-languages",
    async () => languageApi.getLanguages(),
    { staleTime: Infinity }
  );

  const buildDefaultDataTranslations = React.useCallback(() => {
    const translations = data?.map(
      (l) => new DataTranslationValueDto({ languageId: l.id, text: "" })
    );
    return new DataTranslationDto({
      dataTranslationValues: translations,
    });
  }, [data]);

  const currentLanguage = React.useMemo(() => {
    return data?.find(
      (d) => d.code.toLowerCase() === i18n.language.toLowerCase()
    );
  }, [i18n.language, data]);

  const changeLanguage = React.useCallback(
    async (code: string) => {
      await i18n.changeLanguage(code.toLowerCase());
      setLanguageFromStorage(code.toLowerCase());
      dispatchEvent("LanguageChanged");
    },
    [dispatchEvent, i18n, setLanguageFromStorage]
  );

  return {
    languages: data,
    loadingLanguages: isFetching,
    refetchLanguages: refetch,
    buildDefaultDataTranslations,
    currentLanguage,
    changeLanguage,
  };
}
